import type { Bonus } from '../types/content'

export const bonuses: Bonus[] = [
  {
    id: '01',
    title: '150 MTB Drills Library',
    subtitle:
      'The Complete Mountain Bike Drill Bank for Skill Development, Technical Control & Trail Progression',
    story: [
      'Sometimes you don’t need another full session.',
      'You need the right drill for the exact problem in front of you.',
      'A rider is over-braking. Another loses balance. Another keeps missing the line. Another struggles with rear-wheel control.',
      'Instead of inventing another exercise... open the Drill Library.',
    ],
    lists: [
      {
        label: 'You get 150 practical MTB coaching drills covering:',
        items: [
          'Position',
          'Balance',
          'Slow-speed control',
          'Vision',
          'Steering',
          'Braking',
          'Speed control',
          'Cornering',
          'Line choice',
          'Wheel control',
          'Obstacles',
          'Climbing',
          'Traction',
          'Descending',
          'Technical terrain',
          'Flow',
          'Trail decisions',
        ],
      },
    ],
    result: 'Identify the limiter. Choose the drill. Coach the change.',
  },
  {
    id: '02',
    title: 'MTB Coaching Cues & Error Correction Handbook',
    subtitle: '100+ Coaching Cues, Common Rider Mistakes & Fast Correction Strategies',
    story: [
      'The rider makes the same mistake again. And again.',
      'And now they’re looking at you for the answer.',
      'They brake too late. They look at the front wheel. They lose the line. They freeze before the same feature.',
      'Eventually... “Try again” is not enough.',
      'This handbook gives you a practical correction framework:',
    ],
    flow: [
      'Problem',
      'What you see',
      'Likely cause',
      'Cue',
      'Correction',
      'Regression',
      'Progression',
    ],
    lists: [
      {
        label: 'Covering:',
        items: [
          'Rider position',
          'Balance',
          'Vision',
          'Braking',
          'Steering',
          'Cornering',
          'Bike lean',
          'Wheel control',
          'Climbing',
          'Descending',
          'Traction',
          'Pedal timing',
          'Flow',
          'Trail reading',
          'Line choice',
          'Rider decisions',
        ],
      },
    ],
    result: 'Stop simply seeing the error. Know what to do about it.',
  },
  {
    id: '03',
    title: 'MTB Rider Assessment & Skill Level System',
    subtitle:
      'The Complete Rider Evaluation, Progress Tracking & Skill Classification Framework',
    story: [
      'Before asking: “What should I teach?”',
      'Ask: “What does this rider actually need?”',
      'This system helps you evaluate riders across four clear levels:',
    ],
    levels: ['Foundation', 'Developing', 'Confident', 'Advanced'],
    lists: [
      {
        label: 'Assess:',
        items: [
          'Body position',
          'Balance',
          'Slow-speed control',
          'Vision',
          'Braking',
          'Steering',
          'Cornering',
          'Pedal control',
          'Front-wheel control',
          'Rear-wheel control',
          'Climbing',
          'Descending',
          'Technical terrain',
          'Traction',
          'Line choice',
          'Flow',
          'Trail reading',
          'Decision-making',
          'Independence',
        ],
      },
      {
        label: 'Identify:',
        items: [
          'Strongest Skill',
          'Primary Limiter',
          'Secondary Limiter',
          'Confidence Limiter',
          'Next Coaching Priority',
        ],
      },
    ],
    result: 'Stop choosing sessions by instinct. Coach what the rider actually needs.',
  },
  {
    id: '04',
    title: '50 MTB Skills Games & Coaching Challenges',
    subtitle: 'Fun, Competitive & Skill-Based Activities for Individual Riders, Groups & Clinics',
    story: [
      'Technical practice doesn’t have to feel repetitive.',
      'This bonus gives you 50 structured MTB coaching games and challenges designed to make sessions more engaging while still developing real trail skills.',
    ],
    lists: [
      {
        label: 'Use them for:',
        items: [
          'Individual riders',
          'Pairs',
          'Small groups',
          'Clinics',
          'Junior riders',
          'Mixed-level groups',
        ],
      },
      {
        label: 'Develop:',
        items: [
          'Balance',
          'Braking',
          'Vision',
          'Steering',
          'Cornering',
          'Wheel placement',
          'Slow-speed control',
          'Climbing',
          'Descending',
          'Obstacles',
          'Line choice',
          'Flow',
          'Trail reading',
          'Rider decisions',
        ],
      },
    ],
    result: 'Keep riders engaged without sacrificing technical development.',
  },
  {
    id: '05',
    title: '30 MTB Clinics & Ready-to-Run Backup Sessions',
    subtitle: 'Complete Clinic Formats, Mixed-Level Sessions & Last-Minute Coaching Plans',
    story: [
      'Some days your original plan simply doesn’t work.',
      'The rider level changes. The group is different. The terrain isn’t available. Conditions change. You have less time.',
      'Or you just need a complete session immediately.',
      'That’s exactly what this library is for.',
    ],
    lists: [
      {
        label: 'Includes formats such as:',
        items: [
          'Beginner MTB Control Clinic',
          'Balance & Bike Handling Clinic',
          'Braking Clinic',
          'Cornering Clinic',
          'Line Choice Clinic',
          'Technical Climbing Clinic',
          'Descending Control Clinic',
          'Roots & Rocks Clinic',
          'Wheel Control Clinic',
          'Technical Terrain Clinic',
          'Flow & Momentum Clinic',
          'Trail Reading Clinic',
          'Rider Decision-Making Clinic',
          'Mixed-Level Group Clinic',
          'Small-Space Session',
          'Limited-Terrain Session',
          '45-Minute Backup Session',
          '60-Minute Skills Session',
          '90-Minute Trail Clinic',
        ],
      },
    ],
    result: 'Never arrive at a coaching session without a usable plan.',
  },
]
